/*
 * @Author: sybil.chen
 * @Date: 2022-11-14 18:02:24
 * @LastEditTime: 2026-04-08 11:15:58
 * @LastEditors: sybil.chen
 * @Description:
 */
import { getToken } from '@/apis/util';
import { ENV } from '@/config/env';
import { apiHosts } from '@/config/env-website';
import { ProductCodeTypeDict, otherConfigTypeDict, porterageConfigTypeDict } from './constant';
import type {
  BarginPriceInfoType, CommodityPriceItemType, InvoiceItemType, InvoiceTypeType,
  SelectCodeByUserType, AddressType, InsuranceAmountRangeItem
} from './type';
import { fenToYuan, isEmpty } from '@hll/eapp-utils';
import { cloneDeep } from 'lodash-es';
import { wgsTobd } from '@/utils/geo-coord';

export const consignAgreement = {
  url: `${apiHosts.eappweb[ENV]}/#/protocol?token=${getToken()}}`,
  title: '货物托运服务协议',
};

export const infomationService = {
  url: `${apiHosts.eapph5[ENV]
    }/common-privacy?name=info-protocol&needDownload=1&token=${getToken()}`,
  title: '货拉拉企业版信息服务协议',
};

export const consignmentProtocol = {
  url: `${apiHosts.eapph5[ENV]
    }/common-privacy?name=consignment-protocol&token=${getToken()}`,
  title: '货物托运信息服务协议',
};

export const networkProtocol = {
  url: `${apiHosts.eapph5[ENV]
    }/common-privacy?name=network-protocol&needDownload=1&token=${getToken()}`,
  title: '货拉拉企业版网络货运服务协议',
};

/** 通过type转换额外需求的子商品code */
export const getSpecSubCodes = (specReq?: number[]) => {
  return specReq
    ?.map((e) => otherConfigTypeDict.list.find((i) => i.opsType === e)?.key)
    ?.filter((e) => !!e);
};

export const getHandingService = (porterageType: number) => {
  return porterageConfigTypeDict.list.find(
    (i) => i.porterageType === +porterageType
  )?.key;
};

// 获取商品
export const getCommodity = (
  quotationType: SelectCodeByUserType,
  commodityList?: CommodityPriceItemType[]
) => {
  const code = quotationType;
  const commodity = (commodityList || []).find(
    (c) => c.commodityInfo?.productCode === code
  );
  return commodity;
};


/**
 * 两个发票类型是否一致
*/
export const isSameInvoice = ({
  invoice1, invoice2
}: {
  invoice1?: InvoiceItemType
  invoice2?: InvoiceItemType
}) => {
  if (!isEmpty(invoice1?.contractType) && !isEmpty(invoice1?.invoiceType) && !isEmpty(invoice2?.contractType) && !isEmpty(invoice2?.invoiceType))
    return String(invoice1?.invoiceType) === String(invoice2?.invoiceType) &&
      String(invoice1?.contractType) === String(invoice2?.contractType)

  return false;
}

/**
 * 获取invoice的key
*/
export const getInvoiceKey = (invoice?: InvoiceItemType) => {
  if (!isEmpty(invoice?.invoiceType) && !isEmpty(invoice?.contractType)) {
    return `${invoice?.invoiceType}-${invoice?.contractType}`
  }
  return undefined;
}
/**
 * 通过key构造一个invoice结构 InvoiceItemType
*/
export const getInvoiceByKey = (invoiceKey?: string): InvoiceItemType | undefined => {
  const [invoiceType, contractType] = (invoiceKey || '').split('-');
  if (!isEmpty(invoiceType) && !isEmpty(contractType)) {
    return {
      invoiceType: Number(invoiceType),
      contractType: Number(contractType),
    }
  }
  return undefined;
}

/** 纸质回单类型常量 */
export const PAPER_RECEIPT_TYPE = 3;

/**
 * 将搜索接口返回的地址数据转换为内部使用的驼峰命名格式
 * @param obj 搜索接口返回的原始地址数据
 * @param cityId 城市ID（可选，用于覆盖）
 * @returns 转换后的地址数据（AddressType 格式）
 */
export const transformSearchAddressToInternal = async (
  obj: any,
  cityId?: number
): Promise<AddressType> => {
  // WGS84 坐标
  const wgs84 = obj.wgs84Location;
  const latLonBaidu = await wgsTobd(wgs84.lat, wgs84.lon);
  // console.log('transformSearchAddressToInternal:', latLonBaidu);
  return {
    latLon: { lat: wgs84.lat, lon: wgs84.lon },
    latLonBaidu: { lat: latLonBaidu.lat, lon: latLonBaidu.lon },
    latLonGcj: obj.location,
    name: obj.name || '',
    addr: obj.address || obj.addr || '',
    cityId: cityId ?? obj.cityId,
    cityName: obj.city || obj.cityName || '',
    districtName: obj.area || obj.districtName || '',
    houseNumber: obj.houseNumber || obj.orderHouseNumber || '',
    contactsName: obj.contactsName || obj.orderContactName || '',
    contactsPhoneNo: obj.contactsPhoneNo || obj.orderContactPhoneNo || '',
    uid: obj.uid || '',
    poiId: obj.uid || obj.poiId || '',
  };
};

// 议价范围对比
export const checkCommodityValidate = (
  calcData: {
    commodityPriceList: CommodityPriceItemType[];
    currentCode?: string;
    selectedInvoice?: string;
    contractType?: number;
    invoiceType?: InvoiceTypeType;
    insuranceData?: InsuranceAmountRangeItem;
  },
  bargainPrice: (BarginPriceInfoType | undefined)[],
) => {
  const checkValidate = {
    /** 用户议价 - 用户填写价格是否在价格范围内 */
    userBargain: true,
    /** 拼车出价 - 用户填写价格是否在价格范围内 */
    carpoolBargain: true,
    /** 修复了finalprice后的commodity 价格不在范围内直接清空commodity_price_detail */
    repareCommodityPriceList: cloneDeep(calcData.commodityPriceList),
    /** 当前选择的商品是否还在，不在色返回false */
    isCommodityExist: true,
    /** 当前选中的发票类型是否存在，不存在返回false */
    isInvoiceExist: true,
    /** 当前选中的发票类型是否需要重新触发估价，需要返回true */
    isInvoiceNeedCalc: false,
    /** 纸质回单选项是否存在 - 不存在时需要清空纸质回单数据 */
    hasPaperReceipt: true,
    /** 保价的金额是否与预期一致，不一致返回false */
    isInsuranceAmountMatch: true,
    /** 零担拼车出价 - 用户填写价格是否在价格范围内 */
    lingdanPing: true,
  }

  const matchUserSelectCommodity = calcData.currentCode && checkValidate.repareCommodityPriceList.find(e => e.commodityInfo?.productCode === calcData.currentCode);
  checkValidate.isCommodityExist = !calcData.currentCode ? true : !!matchUserSelectCommodity;
  bargainPrice.filter((e) => !!e).forEach(bargainInfo => {

    const bargainCommodity = checkValidate.repareCommodityPriceList.find(commodity => commodity.displayControlFactor?.bargainSupport === 1 && commodity.commodityInfo?.productCode === bargainInfo?.code);
    const rangeCurrent = bargainCommodity?.userQuotationPriceExt;
    if (rangeCurrent && bargainInfo?.quotationPrice) {
      if (
        Number(bargainInfo.quotationPrice) < Number(fenToYuan(rangeCurrent.min || 0)) ||
        Number(bargainInfo.quotationPrice) > Number(fenToYuan(rangeCurrent.max || 0))
      ) {

        if (bargainCommodity.commodityInfo?.productCode === ProductCodeTypeDict.kv.ATTV_PRD_CATE_CARPOOL) {
          checkValidate.carpoolBargain = false;
        } else if(bargainCommodity.commodityInfo?.productCode === ProductCodeTypeDict.kv.ATTV_PRD_CATE_CARGO_PRICING) {
          checkValidate.lingdanPing = false;
        } else {
          checkValidate.userBargain = false;
        }
        // 需要将commodityPrice中的finalprice清空并把价格明细清空 - 直接清空commodity_price_detail
        bargainCommodity.commodityPriceDetail = {
          priceWrappedItem: [],
        };
      }
    }

  });
  const defaultCommodity = checkValidate.repareCommodityPriceList.find(e => e?.displayControlFactor?.selected) || checkValidate.repareCommodityPriceList?.[0];

  const currentCommodity = matchUserSelectCommodity || defaultCommodity;
  if (calcData?.selectedInvoice) {
    const currentInvoice = getInvoiceByKey(calcData?.selectedInvoice);
    const currentInvoiceList = currentCommodity?.invoiceTypeSelectConfig?.invoiceTypeList;
    const isInvoiceExist = currentInvoiceList?.find(e => isSameInvoice({
      invoice1: e,
      invoice2: currentInvoice
    }))
    checkValidate.isInvoiceExist = !!isInvoiceExist;
    if (!isInvoiceExist) {
      // 当长度只有一个时，检查是否和估价返回的发票类型一致，不一致则认为不是使用该发票类型进行估价的，需要清空后重新触发估价
      if (currentInvoiceList?.length === 1) {
        const isSameWithCalc = isSameInvoice({
          invoice1: currentInvoiceList?.[0],
          invoice2: ({ contractType: calcData?.contractType, invoiceType: calcData?.invoiceType as any })
        })
        checkValidate.isInvoiceNeedCalc = !isSameWithCalc;
      }
    }
  }

  // 检查纸质回单选项是否存在
  const specConfigList = currentCommodity?.specConfigList || [];
  checkValidate.hasPaperReceipt = specConfigList.some(
    (item) => item.type === PAPER_RECEIPT_TYPE
  );

  // 检查保价金额是否与预期一致；
  const currentOriginPriceItem = currentCommodity?.commodityPriceDetail?.originPriceItem || [];
  const priceItem88 = currentOriginPriceItem.find(item => item.type === 88);
  const price88Value = priceItem88?.valueFen || 0;
  if(calcData?.insuranceData?.insuranceAmount && price88Value !== calcData?.insuranceData?.insuranceAmount) {
    checkValidate.isInsuranceAmountMatch = false;
  }

  return checkValidate;
}